import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { SavingsTransactionItem } from '@/components/SavingsTransactionItem'
import { ActivityFilters } from '@/components/ActivityFilters'
import { PullToRefresh } from '@/components/PullToRefresh'
import { AppIcons } from '@/components/AppIcons'
import { EmptyActivity, EmptyTransaction } from '@/components/EmptyStates'
import { StickyHeaderNavigation } from '@/components'
import { RangeDate } from '@/domain/transactions'
import { useTheme } from '@/hooks/useTheme'
import { useGetSavingGoal, useGetSavingGoalHistory } from '@/domain/transfer'
import useOffsets from '@/hooks/useOffsets'
import { addDateHeadersToTransactions } from '@/utils/addDateHeadersToTransactions'
import { usePullToRefreshHandler } from '@/hooks'

export const SavingsTransactionsListScreen: React.FC = () => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { goalId } = useParams<{ goalId: string }>()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const offsets = useOffsets()

  // Filter states
  const [searchValue, setSearchValue] = useState('')
  const [dateRange, setDateRange] = useState<RangeDate>({
    startDate: null,
    endDate: null,
  })

  // Get saving goal data for title
  const { data: savingGoal } = useGetSavingGoal({
    saving_goal_id: goalId || '',
  })

  // Fetch saving goal history data (includes all types: transfer_in, transfer_out, earning)
  const {
    data: savingGoalHistoryData,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
  } = useGetSavingGoalHistory({
    saving_goal_id: goalId || '',
    search: searchValue,
    from_date: dateRange.startDate || undefined,
    to_date: dateRange.endDate || undefined,
  })

  const allTransactions = savingGoalHistoryData?.pages || []
  const transactionsWithHeaders = addDateHeadersToTransactions(allTransactions)
  const bothAreEmpty = allTransactions.length === 0

  // Handlers
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const { isRefreshing: isPullRefreshing, handlePullRefresh } =
    usePullToRefreshHandler(refetch)

  // Scroll handling
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = event.currentTarget
    setShowBackToTop(scrollTop > 500)
  }, [])

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Redirect to savings if no goalId
  useEffect(() => {
    if (!goalId) {
      navigate('/savings')
    }
  }, [goalId, navigate])

  if (!goalId) {
    return null
  }

  const pageTitle = savingGoal?.name ? t('activity') : t('savings')

  // Check if completely empty (no transactions and no filters applied)
  // Note: selectedTypes are always set for savings, so we don't count them as "active filters"
  const hasActiveFilters =
    searchValue.trim() !== '' ||
    dateRange.startDate !== null ||
    dateRange.endDate !== null

  const isCompletelyEmpty = bothAreEmpty && !hasActiveFilters && !isLoading

  // Show empty activity state when there are no transactions and no filters
  if (isCompletelyEmpty) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          minHeight: '100vh',
          backgroundColor: 'var(--color-background-screen)',
        }}
      >
        <StickyHeaderNavigation title={pageTitle} />
        <BaseView flex={1} style={{ paddingTop: `${offsets.top + 70}px` }}>
          <EmptyActivity />
        </BaseView>
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        minHeight: '100vh',
        backgroundColor: 'var(--color-background-screen)',
      }}
    >
      <StickyHeaderNavigation title={pageTitle} />

      <PullToRefresh
        onRefresh={handlePullRefresh}
        isRefreshing={isPullRefreshing || isRefetching}
      >
        <BaseView
          style={{
            paddingTop: `${offsets.top + 70}px`,
            paddingBottom: `${offsets.bottom}px`,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Scrollable Content with proper top padding */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {/* Filters */}
            <BaseView px={13}>
              <ActivityFilters
                onSearchChange={setSearchValue}
                selectedTypes={[]} // No type filter needed - savings goal history includes all relevant types
                onTypesChange={() => {}} // Disable type changes - always show savings transactions and daily earnings
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </BaseView>

            {/* Content Area with Pull-to-Refresh */}

            {/* Transactions List or Loading/Error State */}
            {allTransactions.length === 0 && isLoading ? (
              <BaseView px={12}>
                <EmptyTransaction isLoadingTransactions={true} />
              </BaseView>
            ) : allTransactions.length === 0 && isError && !isLoading ? (
              <BaseView center py={40} px={20}>
                <BaseText
                  fontSize={16}
                  fontFamily="regular"
                  color="label.default"
                  textAlign="center"
                  style={{ opacity: 0.7 }}
                >
                  {t('transactionsNotFound')}
                </BaseText>
              </BaseView>
            ) : (
              <BaseView px={13}>
                {transactionsWithHeaders.map((transaction, index) => (
                  <BaseView key={transaction.id}>
                    {/* Date Header */}
                    {transaction.isDifferentDay && (
                      <BaseText
                        fontSize={20}
                        fontFamily="semibold"
                        color="label.default"
                        style={{
                          marginTop:
                            transaction.isDifferentDay && index > 0 ? 24 : 0,
                          marginBottom: 18,
                        }}
                      >
                        {transaction.created_at.toLocaleDateString(
                          i18n.language,
                          {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          },
                        )}
                      </BaseText>
                    )}

                    {/* Transaction Item */}
                    <SavingsTransactionItem transaction={transaction} />
                  </BaseView>
                ))}

                {/* Load More Button */}
                {hasNextPage && (
                  <BaseView center py={20}>
                    <BaseView
                      style={{
                        backgroundColor:
                          theme.colors.background.buttonSecondaryEnabled,
                        padding: '12px 24px',
                        borderRadius: 8,
                        cursor: 'pointer',
                      }}
                      onClick={handleLoadMore}
                    >
                      <BaseText
                        fontSize={14}
                        fontFamily="medium"
                        color="label.clickable"
                      >
                        {isFetchingNextPage ? t('loading') : t('loadMore')}
                      </BaseText>
                    </BaseView>
                  </BaseView>
                )}
              </BaseView>
            )}

            {/* Back to Top Button */}
            {showBackToTop && (
              <BaseView
                style={{
                  position: 'fixed',
                  bottom: '24px',
                  right: '24px',
                  zIndex: 1000,
                }}
              >
                <BaseView
                  circle
                  circleSize={48}
                  center
                  style={{
                    backgroundColor:
                      theme.colors.background.buttonPrimaryEnabled,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                  onClick={scrollToTop}
                >
                  <AppIcons name="chevronUp" size={24} color="label.contrast" />
                </BaseView>
              </BaseView>
            )}
          </div>
        </BaseView>
      </PullToRefresh>
    </div>
  )
}
